import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { Text } from '../Text';

export const DEFAULT_MAX_RECIPIENTS = 10;

const LIST_SEPARATORS = /[,;\n]+/;
// A `Name <addr>` entry, whose name is the run of @-free words before the brackets, or any other whitespace-free run.
const RECIPIENT_TOKEN = /(?:[^\s<>@]+\s+)*[^\s<>@]*<[^>]*>|\S+/g;

export type EmailRejectionReason = 'invalid' | 'duplicate' | 'excluded' | 'limit';

export type EmailRejection = {
  value: string;
  reason: EmailRejectionReason;
};

export type EmailCommitResult = {
  emails: string[];
  rejections: EmailRejection[];
};

export type RecipientAddressRules = {
  /** True when the server would accept `address` as a recipient. */
  isValidAddress: (address: string) => boolean;
  /** Reduces an entry to the bare address the server stores, e.g. `Name <A@x.com>` to `a@x.com`. */
  normalizeAddress: (raw: string) => string;
};

type CommitOptions = RecipientAddressRules & {
  maxRecipients: number;
  excludeAddresses?: string[];
};

/**
 * Splits a typed or pasted list into recipient tokens. Commas, semicolons and newlines always
 * separate; whitespace separates too, except between the words of a `Name <addr>` display name,
 * which would otherwise be shredded into bogus tokens.
 */
export function splitRecipients(raw: string): string[] {
  return raw.split(LIST_SEPARATORS).flatMap((part) => part.match(RECIPIENT_TOKEN) ?? []);
}

/**
 * Appends the recipients in `raw` that pass `isValidAddress` to `current`, reduced by
 * `normalizeAddress`, so with the server's rules nothing the field accepts is later dropped on send.
 *
 * `current` is never mutated, and a rejected entry is returned in `rejections` rather than
 * dropped silently — the caller is expected to tell the user why it did not stick.
 */
export function commitEmails(raw: string, current: string[], options: CommitOptions): EmailCommitResult {
  const excluded = new Set((options.excludeAddresses ?? []).map(options.normalizeAddress));
  const taken = new Set(current.map(options.normalizeAddress));
  const emails = [...current];
  const rejections: EmailRejection[] = [];

  for (const token of splitRecipients(raw)) {
    const address = options.normalizeAddress(token);

    if (!options.isValidAddress(address)) {
      rejections.push({ value: token, reason: 'invalid' });
    } else if (taken.has(address)) {
      rejections.push({ value: address, reason: 'duplicate' });
    } else if (excluded.has(address)) {
      rejections.push({ value: address, reason: 'excluded' });
    } else if (emails.length >= options.maxRecipients) {
      rejections.push({ value: address, reason: 'limit' });
    } else {
      taken.add(address);
      emails.push(address);
    }
  }

  return { emails, rejections };
}

/** The text a commit leaves in the field: a lone malformed entry the user can still fix, otherwise nothing. */
export function draftAfterCommit(rejections: EmailRejection[]): string {
  const [only] = rejections;
  return rejections.length === 1 && only.reason === 'invalid' ? only.value : '';
}

function describeRejection(rejection: EmailRejection, maxRecipients: number): string {
  switch (rejection.reason) {
    case 'invalid':
      return `"${rejection.value}" is not a valid email address.`;
    case 'duplicate':
      return `${rejection.value} has already been added.`;
    case 'excluded':
      return `${rejection.value} already receives this email.`;
    case 'limit':
      return `Only ${maxRecipients} recipients can be added.`;
  }
}

export function describeRejections(rejections: EmailRejection[], maxRecipients: number): string | undefined {
  const [first, ...rest] = rejections;
  if (!first) return undefined;
  const head = describeRejection(first, maxRecipients);
  return rest.length > 0 ? `${head} ${rest.length} more not added.` : head;
}

const fieldVariants = cva(
  'flex w-full flex-wrap items-center gap-1 rounded border bg-white px-2 py-1 transition-[color,box-shadow,border-color] focus-within:border-focus dark:bg-white/30',
  {
    variants: {
      state: {
        default: 'border-grey-30',
        invalid: 'border-destructive',
        disabled: 'cursor-not-allowed border-grey-30 bg-grey-10 opacity-50',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface EmailRecipientsInputProps extends RecipientAddressRules {
  value: string[];
  onChange: (emails: string[]) => void;
  /** Fires on every draft change, including the rewrite a commit performs to clear or reject it. */
  onDraftChange?: (draft: string) => void;
  /** Defaults to 10. */
  maxRecipients?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Addresses rejected on entry with a message, e.g. the requester's own. */
  excludeAddresses?: string[];
  /** Prefixes the input, each chip, and each remove button test id. */
  testIdPrefix: string;
  /** Id of the consumer's own visible label; replaces the built-in `aria-label` when set. */
  'aria-labelledby'?: string;
  className?: string;
}

function EmailRecipientsInput({
  value,
  onChange,
  onDraftChange,
  isValidAddress,
  normalizeAddress,
  maxRecipients = DEFAULT_MAX_RECIPIENTS,
  placeholder = 'Add an email address',
  disabled = false,
  excludeAddresses,
  testIdPrefix,
  'aria-labelledby': ariaLabelledBy,
  className,
}: EmailRecipientsInputProps) {
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string>();
  const messageId = React.useId();

  const isFull = value.length >= maxRecipients;
  // Read-only once the cap is reached and nothing is left to fix keeps focus in the field and leaves Backspace able to free a slot.
  const isLocked = isFull && !draft;
  const message = error ?? (isFull ? `Maximum of ${maxRecipients} recipients reached.` : undefined);

  const updateDraft = (next: string) => {
    setDraft(next);
    onDraftChange?.(next);
  };

  const commitDraft = (raw: string) => {
    if (!raw.trim()) {
      updateDraft('');
      return;
    }
    const { emails, rejections } = commitEmails(raw, value, {
      maxRecipients,
      excludeAddresses,
      isValidAddress,
      normalizeAddress,
    });
    if (emails.length !== value.length) onChange(emails);
    updateDraft(draftAfterCommit(rejections));
    setError(describeRejections(rejections, maxRecipients));
  };

  const removeRecipient = (email: string) => {
    setError(undefined);
    onChange(value.filter((entry) => entry !== email));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter that confirms an IME candidate is part of typing, not a commit.
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      commitDraft(draft);
      return;
    }
    // A forward Tab commits in place once; with a rejection already showing it moves on.
    if (event.key === 'Tab' && !event.shiftKey && draft.trim() && !error) {
      event.preventDefault();
      commitDraft(draft);
      return;
    }
    if (event.key === 'Backspace' && !draft && value.length > 0) {
      removeRecipient(value[value.length - 1]);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (splitRecipients(pasted).length < 2) return;
    event.preventDefault();
    commitDraft(`${draft} ${pasted}`);
  };

  return (
    <div data-slot="email-recipients-input" className={cn('flex w-full flex-col gap-1', className)}>
      <div className={fieldVariants({ state: disabled ? 'disabled' : error ? 'invalid' : 'default' })}>
        {value.map((email) => (
          <span
            key={email}
            data-slot="email-recipients-chip"
            data-testid={`${testIdPrefix}-chip-${email}`}
            className="flex max-w-full items-center gap-0.5 rounded-lg border border-blue-20 bg-blue-10 py-0.5 pl-2 pr-1"
          >
            <Text className="truncate text-xs font-medium text-blue-60">{email}</Text>
            <IconButton
              type="button"
              variant="hollow"
              size="icon"
              icon="cross2"
              iconSize={10}
              disabled={disabled}
              aria-label={`Remove ${email}`}
              data-testid={`${testIdPrefix}-chip-remove-${email}`}
              onClick={() => removeRecipient(email)}
              className="rounded-full text-blue-60 hover:bg-blue-20"
            />
          </span>
        ))}
        <input
          type="email"
          data-slot="email-recipients-field"
          data-testid={`${testIdPrefix}-input`}
          className="h-6 min-w-[10rem] flex-1 bg-transparent text-xs leading-[1.4] text-grey-80 placeholder:text-grey-50 outline-none disabled:cursor-not-allowed"
          value={draft}
          placeholder={isFull ? '' : placeholder}
          disabled={disabled}
          readOnly={isLocked}
          aria-disabled={isLocked || undefined}
          autoComplete="off"
          aria-label={ariaLabelledBy ? undefined : 'Email recipients'}
          aria-labelledby={ariaLabelledBy}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          onChange={(event) => {
            updateDraft(event.target.value);
            setError(undefined);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => commitDraft(draft)}
        />
      </div>

      {/* Always mounted so the live region exists before it has anything to announce. */}
      <Text
        id={messageId}
        role="status"
        aria-live="polite"
        data-testid={`${testIdPrefix}-message`}
        className={cn('min-h-4 text-xs', error ? 'text-destructive' : 'text-grey-60')}
      >
        {message}
      </Text>
    </div>
  );
}

export { EmailRecipientsInput };
